/* eslint-disable react/prop-types */
import React from "react";
import { Link } from "react-router-dom";
import { LogOut, Mail, ShieldCheck } from "lucide-react";

import { supabase } from "@/lib/supabaseClient";
import {
  MARKETING_CONSENT_COPY,
  MARKETING_CONSENT_VERSION,
  PRIVACY_NOTICE_PATH,
  TERMS_PATH,
  TERMS_VERSION,
} from "@/config/legal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

function LoadingState() {
  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4">
      <p className="font-sans text-sm text-black/60">Opening your collection…</p>
    </div>
  );
}

export default function MemberConsentGate({ children }) {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [accepted, setAccepted] = React.useState(false);
  const [termsAccepted, setTermsAccepted] = React.useState(false);
  const [marketingOptIn, setMarketingOptIn] = React.useState(false);
  const [locale, setLocale] = React.useState("en");
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let alive = true;

    (async () => {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) throw sessionError;
      if (!session?.user) return;

      const { data: profile, error: profileError } = await supabase
        .from("users")
        .select(
          "terms_version,terms_accepted_at,marketing_opt_in,marketing_consent_version,marketing_consent_updated_at,language"
        )
        .eq("auth_user_id", session.user.id)
        .maybeSingle();

      if (profileError) throw profileError;
      if (!alive) return;

      setAccepted(
        profile?.terms_version === TERMS_VERSION &&
          Boolean(profile?.terms_accepted_at)
      );
      setMarketingOptIn(
        profile?.marketing_opt_in === true &&
          profile?.marketing_consent_version === MARKETING_CONSENT_VERSION &&
          Boolean(profile?.marketing_consent_updated_at)
      );
      setLocale(profile?.language === "nl" ? "nl" : "en");
    })()
      .catch((loadError) => {
        console.error("Could not load member agreement:", loadError);
        if (alive) {
          setError(
            "We could not open your membership details. Please try again."
          );
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const syncMarketingPreference = React.useCallback(async (accessToken) => {
    try {
      const response = await fetch("/api/memberMarketingConsent", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        console.warn("Marketing preference could not be synced right now.");
      }
    } catch (syncError) {
      // Membership must never be blocked by an optional marketing integration.
      console.warn("Marketing preference sync failed:", syncError);
    }
  }, []);

  const handleContinue = async () => {
    if (!termsAccepted || saving) return;

    setSaving(true);
    setError("");

    try {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) throw sessionError;
      if (!session?.access_token) throw new Error("Your session has expired.");

      const { error: consentError } = await supabase.rpc(
        "complete_member_onboarding",
        {
          p_accept_terms: true,
          p_marketing_opt_in: marketingOptIn,
          p_locale: locale,
        }
      );

      if (consentError) throw consentError;

      setAccepted(true);
      void syncMarketingPreference(session.access_token);
    } catch (saveError) {
      console.error("Could not save member agreement:", saveError);
      setError(
        saveError?.message ||
          "We could not save your choices. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.assign("/signin");
  };

  if (loading) return <LoadingState />;
  if (accepted) return children;

  return (
    <main className="mx-auto flex min-h-[100dvh] w-full max-w-3xl items-center px-4 py-10 font-sans sm:py-16">
      <Card className="w-full overflow-hidden rounded-3xl border-2 border-white bg-white/60 shadow-none">
        <CardHeader className="space-y-5 bg-background/0 px-6 py-8 text-black sm:px-10 sm:pt-10">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white/60 ">
            <ShieldCheck className="h-5 w-5" aria-hidden="true" />
          </div>

          <div>
            <p className="mb-3 text-xs uppercase tracking-[0.18em] text-black/40">
              privacy & Security
            </p>
            <CardTitle className="font-kalice text-4xl font-medium leading-tight sm:text-5xl">
              Welcome to your collection.
            </CardTitle>
            <p className="mt-4 max-w-xl text-base leading-tight text-black/40">
              Palais Antoia brings together your personal creations and experiences. Before entering your collection, please review the
              membership terms.
            </p>
          </div>
        </CardHeader>

        <CardContent className="space-y-4 px-6 pt-4 pb-8 sm:px-10 sm:pb-10">
          <div className="rounded-2xl border border-black/10 bg-white/70 p-5">
            <div className="flex items-start gap-3">
              <Checkbox
                id="member-terms"
                checked={termsAccepted}
                onCheckedChange={(value) => setTermsAccepted(value === true)}
                className="mt-0.5 border-black/40 data-[state=checked]:bg-black data-[state=checked]:text-white"
              />
              <div className="space-y-2">
                <Label
                  htmlFor="member-terms"
                  className="cursor-pointer text-base font-medium leading-snug"
                >
                  I agree to the Antoia Membership Terms.
                </Label>
                <p className="text-sm leading-relaxed text-black/60">
                  Read the{" "}
                  <Link
                    to={TERMS_PATH}
                    target="_blank"
                    rel="noreferrer"
                    className="underline underline-offset-4 hover:text-black"
                  >
                    Membership Terms
                  </Link>
                  . How Antoia uses and protects your information is explained
                  in our{" "}
                  <Link
                    to={PRIVACY_NOTICE_PATH}
                    target="_blank"
                    rel="noreferrer"
                    className="underline underline-offset-4 hover:text-black"
                  >
                    Privacy Notice
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-black/10 p-5">
            <div className="flex items-start gap-3">
              <Checkbox
                id="member-marketing"
                checked={marketingOptIn}
                onCheckedChange={(value) => setMarketingOptIn(value === true)}
                className="mt-0.5 border-black/40 data-[state=checked]:bg-black data-[state=checked]:text-white"
              />
              <div className="space-y-2">
                <Label
                  htmlFor="member-marketing"
                  className="flex cursor-pointer items-center gap-2 text-base font-medium leading-snug"
                >
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  Receive invitations and updates
                </Label>
                <p className="text-sm leading-relaxed text-black/60">
                  {MARKETING_CONSENT_COPY}
                </p>
              </div>
            </div>
          </div>

          {error ? (
            <p className="text-sm text-red-700" role="alert">
              {error}
            </p>
          ) : null}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={handleSignOut}
              className="justify-start rounded-xl px-0 text-black/50 hover:bg-transparent hover:text-black"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </Button>

            <Button
              type="button"
              onClick={handleContinue}
              disabled={!termsAccepted || saving}
              className="rounded-xl px-8 py-6"
            >
              {saving ? "Saving…" : "Enter my collection"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}
